import React from 'react';
import {
  FormGroup,
  InputGroup,
  NumericInput,
  Checkbox,
  RadioGroup,
  Switch,
  EditableText,
  TextArea,
  HTMLSelect,
} from '@blueprintjs-formik/core';
import { MultiSelect, SuggestField } from '@blueprintjs-formik/select';
import { LocalizedFDateInput } from './LocalizedDateInputs';
import { FSelect } from './Select';
import { useAppIntlContext } from '../AppIntlProvider';

// Wrap the formik NumericInput so the underlying Blueprint input picks the
// active intl locale by default — FR users typing "1234,56" must not get NaN.
// Reads locale from React context (not `intl.getInitOptions()`) so the value
// is right on first paint and inputs don't flash unformatted before locale
// formatting kicks in.
const FNumericInput = (props: any) => {
  const { currentLocale } = useAppIntlContext() || {};
  const locale = props.locale ?? currentLocale ?? navigator.language ?? 'en';
  return <NumericInput {...props} locale={locale} />;
};

// The formik `Switch`'s `label` prop is typed as `string` only, but Blueprint's
// underlying Switch accepts any `React.ReactNode` at runtime (consumers pass a
// localized `<T />` element). Widen the `label` type here without changing
// runtime behaviour.
const FSwitch = (props: any) => <Switch {...props} />;

// The formik `Checkbox`'s `label` prop is typed as `string` only, but Blueprint's
// underlying Checkbox accepts any `React.ReactNode` at runtime (consumers pass a
// localized `<T />` element). Widen the props here without changing runtime
// behaviour.
const FCheckboxWidened = (props: any) => <Checkbox {...props} />;

// Blueprint-formik's `TextArea` props type omits the passthrough props consumers
// rely on at runtime (e.g. `growVertically`, `height`, `fill`, `fastField`),
// which are forwarded to the underlying Blueprint TextArea. Widen the props here
// without changing runtime behaviour.
const FTextAreaWidened = (props: any) => <TextArea {...props} />;

// The `*Accessor` props of the underlying `@blueprintjs-formik/select`
// components are typed as `string` only, but `getAccessor` supports passing a
// function `(item) => value` at runtime (see the library's utils.getAccessor).
// Widen the accessor prop types here so consumers can pass display-name
// resolvers without changing runtime behaviour.
type AccessorProp = string | ((item: any) => React.ReactNode);

// The item type of these selects is domain-specific (accounts, contacts,
// currencies, …) and the library's own props interface is not exported, so
// item-related callbacks are typed as `any` here. Accessors are widened to
// accept resolver functions, and an index signature forwards the remaining
// (fully typed upstream) Blueprint MultiSelect/Suggest props.
interface WidenedSelectProps {
  name?: string;
  items?: any[];
  valueAccessor?: AccessorProp;
  labelAccessor?: AccessorProp;
  textAccessor?: AccessorProp;
  tagAccessor?: AccessorProp;
  itemRenderer?: (item: any, itemProps: any, state: any) => JSX.Element | null;
  itemPredicate?: (
    query: string,
    item: any,
    index?: number,
    exactMatch?: boolean,
  ) => boolean;
  onItemSelect?: (item: any, event?: React.SyntheticEvent<HTMLElement>) => void;
  onCreateItemSelect?: (
    item?: any,
    event?: React.SyntheticEvent<HTMLElement>,
  ) => void;
  createNewItemRenderer?: (
    query: string,
    active: boolean,
    handleClick: React.MouseEventHandler<HTMLElement>,
  ) => JSX.Element | undefined;
  createNewItemFromQuery?: (query: string) => any;
  selectedItems?: any[];
  tagRenderer?: (item: any) => React.ReactNode;
  className?: string;
  [key: string]: any;
}

const FMultiSelect = MultiSelect as unknown as (
  props: WidenedSelectProps,
) => JSX.Element;

const FSuggest = SuggestField as unknown as (
  props: WidenedSelectProps,
) => JSX.Element;

// Blueprint-formik's `FormGroup` props type omits the passthrough props that
// consumers rely on at runtime (e.g. `fastField`, `fill`) — they are forwarded
// to the underlying Blueprint FormGroup, which ignores unknown props. Widen the
// props here without changing runtime behaviour.
const FFormGroupWidened = (props: any) => <FormGroup {...props} />;

// Same rationale as FFormGroupWidened: the blueprint-formik `InputGroup` props
// type omits passthrough props consumers rely on (e.g. `minimal`, `fill`), which
// are forwarded to the underlying Blueprint InputGroup. Widen without changing
// runtime behaviour.
const FInputGroupWidened = (props: any) => <InputGroup {...props} />;

export {
  FFormGroupWidened as FFormGroup,
  FInputGroupWidened as FInputGroup,
  FNumericInput,
  FCheckboxWidened as FCheckbox,
  RadioGroup as FRadioGroup,
  FSwitch,
  FSelect,
  FMultiSelect,
  EditableText as FEditableText,
  FSuggest,
  FTextAreaWidened as FTextArea,
  LocalizedFDateInput as FDateInput,
  HTMLSelect as FHTMLSelect,
};
